import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { InlineLoader } from "@/components/ui/pulsing-loader";
import { useAuth } from "@/context/authContext";
import { useInstitution, useInstitutionMutations } from "../hooks";
import { getInstitutionConfig } from "../config/institutions";
import { InstitutionBanner } from "../components/InstitutionBanner";
import { InstitutionAuthoritiesTab } from "../components/InstitutionAuthoritiesTab";
import { InstitutionMembersTab } from "../components/InstitutionMembersTab";
import { InstitutionCredentialsTab } from "../components/InstitutionCredentialsTab";
import { InstitutionSettingsTab } from "../components/InstitutionSettingsTab";
import { getUserProfileByAuthId } from "@/Services/users.service";
import { isAdmin } from "@/models/user";
import { useEffect, useState } from "react";
import type { User } from "@/models/user";
import { Users2 } from "lucide-react";

type Tab = "authorities" | "members" | "credentials" | "settings";

export function InstitutionDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { user: authUser } = useAuth();

  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>("authorities");

  const { data: institution, isLoading, isError } = useInstitution(slug);
  const config = slug ? getInstitutionConfig(slug) : undefined;
  const { updateInstitution } = useInstitutionMutations(
    institution?.id ?? "",
  );

  const userIsAdmin = isAdmin(currentUser);

  useEffect(() => {
    if (!authUser) return;
    getUserProfileByAuthId(authUser.id)
      .then(setCurrentUser)
      .catch(() => setCurrentUser(null));
  }, [authUser]);

  if (isLoading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <InlineLoader message="Cargando institución..." />
      </div>
    );
  }

  if (isError || !institution) {
    return (
      <div className="flex h-96 flex-col items-center justify-center gap-4 p-6">
        <p className="text-lg font-semibold text-muted-foreground">
          Institución no encontrada
        </p>
        <Button variant="outline" onClick={() => navigate(-1)}>
          <ArrowLeft className="mr-2 size-4" />
          Volver
        </Button>
      </div>
    );
  }

  const handleGradientChange = async (gradient: string) => {
    if (!authUser) return;
    await updateInstitution({ gradient }, authUser.id);
  };

  return (
    <div className="space-y-6 p-6">
      <Button variant="ghost" onClick={() => navigate(-1)} className="-mb-2">
        <ArrowLeft className="mr-2 size-4" />
        Volver
      </Button>

      {/* Banner — always visible */}
      <InstitutionBanner
        institution={institution}
        icon={config?.icon ?? Users2}
        isAdmin={userIsAdmin}
        onGradientChange={handleGradientChange}
      />

      {/* Tabs */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, delay: 0.15 }}
      >
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as Tab)}>
          <TabsList className={userIsAdmin ? "grid w-full grid-cols-4" : "grid w-full grid-cols-2"}>
            <TabsTrigger value="authorities">Autoridades</TabsTrigger>
            <TabsTrigger value="members">Miembros</TabsTrigger>
            {userIsAdmin && (
              <TabsTrigger value="credentials">Credenciales</TabsTrigger>
            )}
            {userIsAdmin && (
              <TabsTrigger value="settings">Configuración</TabsTrigger>
            )}
          </TabsList>

          <div className="mt-6">
            <TabsContent value="authorities">
              <InstitutionAuthoritiesTab
                institutionId={institution.id}
                isAdmin={userIsAdmin}
              />
            </TabsContent>

            <TabsContent value="members">
              <InstitutionMembersTab
                institutionId={institution.id}
                isAdmin={userIsAdmin}
              />
            </TabsContent>

            {userIsAdmin && (
              <TabsContent value="credentials">
                <InstitutionCredentialsTab institutionId={institution.id} isAdmin={userIsAdmin} />
              </TabsContent>
            )}

            {userIsAdmin && (
              <TabsContent value="settings">
                <InstitutionSettingsTab institution={institution} />
              </TabsContent>
            )}
          </div>
        </Tabs>
      </motion.div>
    </div>
  );
}
